import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { setupAppPageBodyBackground } from "../game/gameStageBackground";
import {
  STAGE_ROW_KEY,
  ensureStageRowId,
  ensureStageSessionForUser,
  saveGameStageAndReport,
} from "../../functions/stageReportSync";
import StageTimer from "../game/StageTimer";
import greenBtn from "../img/green_button.png";
import {
  formatSecondsToClock,
  loadHallElapsedFromDb,
  persistHallElapsedTime,
  readTotalScoreFromStage,
  liveProgressPoints,
} from "./hallTimer";
import {
  LOCKERS,
  ATHLETE_NAMES,
  LOCKER_ARCHIVE_POINTS,
  LOCKER_ARCHIVE_UNLOCKS,
  shuffleArray,
  validateLockerPlacements,
  assignNameToLocker,
  clearLockerAssignment,
} from "./lockerArchiveData";
import { unlockHall1Hotspot, markHall1HotspotCompleted, getRedirectIfHall1StageDone } from "./medalDisplayData";
import { createDragAutoScroller } from "./dragDropHelpers";
import deskBg from "./stage3/h1_stage3.jpg";
import mobBg from "./stage3/h1_stage3_mob.jpg";
import nameBg from "./stage3/name_bg.png";
import objectiveBox from "./stage3/box.png";
import titlePlate from "./stage3/b2_button.png";
import "../fonts/breuer-headline.css";
import "./stage3.css";

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function NameTag({ athlete, lifted, wrong, locked, onPointerDown, compact }) {
  return (
    <button
      type="button"
      className={`locker-name${lifted ? " locker-name--lifted" : ""}${
        wrong ? " locker-name--wrong" : ""
      }${locked ? " locker-name--locked" : ""}${
        compact ? " locker-name--compact" : ""
      }`}
      style={{ backgroundImage: `url(${nameBg})` }}
      onPointerDown={(e) => {
        if (locked) return;
        onPointerDown?.(e, athlete.id);
      }}
      aria-label={athlete.name}
      aria-grabbed={lifted ? "true" : "false"}
      aria-disabled={locked ? "true" : undefined}
    >
      <span className="locker-name__text">{athlete.name}</span>
    </button>
  );
}

function DragGhost({ athlete, x, y }) {
  if (!athlete) return null;
  return (
    <div
      className="locker-drag-ghost"
      style={{
        left: x,
        top: y,
        backgroundImage: `url(${nameBg})`,
      }}
      aria-hidden
    >
      <span className="locker-name__text">{athlete.name}</span>
    </div>
  );
}

/** Hall Stage 3 — Champions’ Locker Archive (Museum Archive) */
export default function HallStage3() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { user } = useSelector((s) => s.auth);
  const storedUser = useMemo(() => user || getStoredUser(), [user]);
  const isDemoBypass = storedUser?.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const athleteById = useMemo(
    () => Object.fromEntries(ATHLETE_NAMES.map((a) => [a.id, a])),
    []
  );
  const lockerById = useMemo(
    () => Object.fromEntries(LOCKERS.map((l) => [l.id, l])),
    []
  );

  const [nameOrder] = useState(() =>
    shuffleArray(ATHLETE_NAMES.map((a) => a.id))
  );
  const [placements, setPlacements] = useState(() =>
    Object.fromEntries(LOCKERS.map((l) => [l.id, null]))
  );
  const [lockedLockerIds, setLockedLockerIds] = useState([]);
  const [drag, setDrag] = useState(null);
  const [dropTargetId, setDropTargetId] = useState(null);
  const [inspectId, setInspectId] = useState(null);
  const [feedback, setFeedback] = useState({ wrong: [], correct: [] });
  const [status, setStatus] = useState("playing");
  const [errorMsg, setErrorMsg] = useState("");
  const [elapsedSec, setElapsedSec] = useState(0);
  const [totalScore, setTotalScore] = useState(0);
  const [timerReady, setTimerReady] = useState(false);
  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || ""
  );

  const elapsedRef = useRef(0);
  const dragRef = useRef(null);
  const placementsRef = useRef(placements);
  const lockedRef = useRef([]);
  const completingRef = useRef(false);
  const autoScrollRef = useRef(null);
  if (!autoScrollRef.current) {
    autoScrollRef.current = createDragAutoScroller();
  }

  useEffect(() => {
    placementsRef.current = placements;
  }, [placements]);

  useEffect(() => {
    lockedRef.current = lockedLockerIds;
  }, [lockedLockerIds]);

  const liveStagePoints = useMemo(
    () =>
      liveProgressPoints(
        lockedLockerIds.length,
        LOCKERS.length,
        LOCKER_ARCHIVE_POINTS
      ),
    [lockedLockerIds.length]
  );

  const displayPoints =
    status === "success" ? totalScore : totalScore + liveStagePoints;

  useEffect(() => {
    dragRef.current = drag;
  }, [drag]);

  useEffect(() => {
    dispatch(setBackButtonUrl("/hall-1"));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("hall1-stage3-page");
    const cleanupBg = setupAppPageBodyBackground(document.body, {
      deskForeground: deskBg,
      mobForeground: mobBg,
    });
    return () => {
      cleanupBg?.();
      document.body.classList.remove("hall1-stage3-page");
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      ensureStageSessionForUser(storedUser);
      const { stageId, elapsedSec: sec, stage } = await loadHallElapsedFromDb({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
      });
      if (cancelled) return;

      const redirect = getRedirectIfHall1StageDone(stage, 3);
      if (redirect) {
        navigate(redirect, { replace: true });
        return;
      }

      if (stageId) setStageRowId(String(stageId));
      elapsedRef.current = sec;
      setElapsedSec(sec);
      setTotalScore(readTotalScoreFromStage(stage));
      setTimerReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [backendBase, adminToken, storedUser, isDemoBypass, navigate]);

  useEffect(() => {
    if (!timerReady || status === "success") return undefined;
    const tick = window.setInterval(() => {
      setElapsedSec((s) => {
        const next = s + 1;
        elapsedRef.current = next;
        return next;
      });
    }, 1000);
    return () => clearInterval(tick);
  }, [timerReady, status]);

  useEffect(() => {
    if (!timerReady) return undefined;
    const save = () => {
      persistHallElapsedTime({
        backendBase,
        adminToken,
        storedUser,
        isDemoBypass,
        stageId: stageRowId || sessionStorage.getItem(STAGE_ROW_KEY),
        elapsedSec: elapsedRef.current,
      });
    };
    const id = window.setInterval(save, 15000);
    return () => {
      clearInterval(id);
      if (status === "playing") save();
    };
  }, [
    timerReady,
    backendBase,
    adminToken,
    storedUser,
    isDemoBypass,
    stageRowId,
    status,
  ]);

  const clearDrag = useCallback(() => {
    autoScrollRef.current?.stop();
    setDrag(null);
    setDropTargetId(null);
    document.body.classList.remove("locker-dragging");
  }, []);

  useEffect(() => () => clearDrag(), [clearDrag]);

  const lockCorrectLockers = useCallback((nextPlacements) => {
    const toLock = LOCKERS.filter(
      (l) =>
        nextPlacements[l.id] === l.athleteId &&
        !lockedRef.current.includes(l.id)
    ).map((l) => l.id);
    if (!toLock.length) return;
    setLockedLockerIds((prev) => {
      const set = new Set(prev);
      toLock.forEach((id) => set.add(id));
      return [...set];
    });
  }, []);

  const onPointerDownName = useCallback(
    (e, athleteId) => {
      if (status === "success" || status === "saving") return;
      if (e.button != null && e.button !== 0) return;

      let originLocker = null;
      Object.entries(placementsRef.current).forEach(([lockerId, aid]) => {
        if (aid === athleteId) originLocker = lockerId;
      });
      if (originLocker && lockedRef.current.includes(originLocker)) return;

      const el = e.currentTarget;
      const rect = el.getBoundingClientRect();

      e.preventDefault();
      el.setPointerCapture?.(e.pointerId);

      setDrag({
        athleteId,
        originLocker,
        pointerId: e.pointerId,
        offsetX: e.clientX - rect.left,
        offsetY: e.clientY - rect.top,
        x: rect.left,
        y: rect.top,
      });
      setFeedback({ wrong: [], correct: [] });
      setErrorMsg("");
      document.body.classList.add("locker-dragging");
      autoScrollRef.current?.update(e.clientY);
    },
    [status]
  );

  const tryDropOnLocker = useCallback(
    (athleteId, lockerId, originLocker) => {
      const locker = lockerById[lockerId];
      if (!locker) {
        clearDrag();
        return;
      }

      if (lockedRef.current.includes(lockerId)) {
        clearDrag();
        return;
      }

      if (originLocker === lockerId) {
        clearDrag();
        return;
      }

      const next = assignNameToLocker(
        placementsRef.current,
        lockerId,
        athleteId,
        originLocker
      );
      placementsRef.current = next;
      setPlacements(next);
      lockCorrectLockers(next);
      setFeedback({ wrong: [], correct: [] });
      setErrorMsg("");
      clearDrag();
    },
    [lockerById, clearDrag, lockCorrectLockers]
  );

  useEffect(() => {
    const onMove = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.update(e.clientY);

      setDrag((prev) =>
        prev
          ? {
              ...prev,
              x: e.clientX - prev.offsetX,
              y: e.clientY - prev.offsetY,
            }
          : prev
      );

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const lockerEl = el?.closest?.("[data-locker-slot]");
      setDropTargetId(lockerEl?.getAttribute("data-locker-slot") || null);
    };

    const onUp = (e) => {
      const d = dragRef.current;
      if (!d || e.pointerId !== d.pointerId) return;

      autoScrollRef.current?.stop();

      const el = document.elementFromPoint(e.clientX, e.clientY);
      const lockerEl = el?.closest?.("[data-locker-slot]");
      const poolEl = el?.closest?.("[data-name-pool]");

      if (lockerEl) {
        tryDropOnLocker(
          d.athleteId,
          lockerEl.getAttribute("data-locker-slot"),
          d.originLocker
        );
      } else if (poolEl) {
        if (d.originLocker) {
          setPlacements((prev) =>
            clearLockerAssignment(prev, d.originLocker)
          );
        }
        clearDrag();
      } else {
        clearDrag();
      }
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    return () => {
      autoScrollRef.current?.stop();
      clearDrag();
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
    };
  }, [tryDropOnLocker, clearDrag]);

  const handleSubmit = useCallback(async () => {
    if (status === "success" || status === "saving" || completingRef.current) {
      return;
    }

    const result = validateLockerPlacements(placements);
    if (!result.ok) {
      setFeedback({
        wrong: [...result.wrongLockerIds, ...result.missingLockerIds],
        correct: LOCKERS.filter(
          (l) =>
            placements[l.id] &&
            !result.wrongLockerIds.includes(l.id) &&
            !result.missingLockerIds.includes(l.id)
        ).map((l) => l.id),
      });
      setErrorMsg(
        result.missingLockerIds.length
          ? "Assign a nameplate to every locker, then try again."
          : "Some nameplates don’t match the memorabilia. Try again."
      );
      return;
    }

    completingRef.current = true;
    setFeedback({
      wrong: [],
      correct: LOCKERS.map((l) => l.id),
    });
    setErrorMsg("");
    setStatus("saving");

    const timeStr = formatSecondsToClock(elapsedRef.current);
    const answers = {
      puzzle: "locker_archive",
      placements,
    };

    try {
      let stageId = stageRowId || sessionStorage.getItem(STAGE_ROW_KEY);
      if (!stageId && !isDemoBypass && backendBase && adminToken) {
        stageId = await ensureStageRowId({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
        });
        if (stageId) setStageRowId(String(stageId));
      }

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        const result = await saveGameStageAndReport({
          backendBase,
          adminToken,
          storedUser,
          isDemoBypass,
          stageId,
          stageNumber: 3,
          score: LOCKER_ARCHIVE_POINTS,
          answers,
          time: timeStr,
        });
        if (result?.total_score != null) {
          setTotalScore(Number(result.total_score) || 0);
        } else {
          setTotalScore((prev) => prev + LOCKER_ARCHIVE_POINTS);
        }
      } else {
        setTotalScore((prev) => prev + LOCKER_ARCHIVE_POINTS);
      }

      markHall1HotspotCompleted("museum_archive");
      unlockHall1Hotspot(LOCKER_ARCHIVE_UNLOCKS);
      setStatus("success");
    } catch (err) {
      console.error("Locker archive save:", err);
      completingRef.current = false;
      setStatus("playing");
      setErrorMsg("Could not save progress. Please try again.");
    }
  }, [
    status,
    placements,
    stageRowId,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
  ]);

  const inspectLocker = inspectId ? lockerById[inspectId] : null;
  const dragAthlete = drag ? athleteById[drag.athleteId] : null;

  return (
    <div className="hall1-stage3 locker-archive">
      <div className="stage-escape-hud">
        <StageTimer
          timeLabel={formatSecondsToClock(elapsedSec)}
          points={displayPoints}
        />
      </div>

      <div className="locker-archive__inner">
        <header className="locker-archive__header">
          <div
            className="locker-archive__title-plate"
            style={{ backgroundImage: `url(${titlePlate})` }}
          >
            {/* <span className="locker-archive__eyebrow"></span> */}
            <h1 className="locker-archive__title">CHAMPION LOCKER ARCHIVE</h1>
          </div>
        </header>

        <div className="locker-archive__layout">
          <aside
            className="locker-archive__objective"
            style={{ backgroundImage: `url(${objectiveBox})` }}
          >
            <h2 className="locker-archive__objective-title">OBJECTIVE</h2>
            <p className="locker-archive__objective-text">
            The athlete nameplates have fallen off. Match each locker to its rightful champion.
            </p>
            <hr className="locker-archive__objective-rule" />
            <p className="locker-archive__objective-hint">
              Check the clues inside the locker to help you.
            </p>
          </aside>

          <div className="locker-archive__cases" role="list">
            {LOCKERS.map((locker) => {
              const assignedId = placements[locker.id];
              const assigned = assignedId ? athleteById[assignedId] : null;
              const isOver = dropTargetId === locker.id && drag;
              const isWrong = feedback.wrong.includes(locker.id);
              const isCorrect =
                feedback.correct.includes(locker.id) ||
                lockedLockerIds.includes(locker.id);
              const isLocked = lockedLockerIds.includes(locker.id);

              return (
                <div
                  key={locker.id}
                  role="listitem"
                  data-locker-slot={locker.id}
                  className={`locker-case${isOver && !isLocked ? " locker-case--over" : ""}${
                    isWrong ? " locker-case--wrong" : ""
                  }${isCorrect ? " locker-case--correct" : ""}`}
                >
                  <button
                    type="button"
                    className="locker-case__inspect"
                    onClick={() => {
                      if (drag) return;
                      setInspectId(locker.id);
                    }}
                    aria-label={`Inspect locker ${locker.number}`}
                  >
                    <img
                      src={locker.image}
                      alt=""
                      className="locker-case__img"
                      draggable={false}
                    />
                    <span className="locker-case__number">{locker.number}</span>
                  </button>

                  <div className="locker-case__slot">
                    {assigned ? (
                      <NameTag
                        athlete={assigned}
                        compact
                        locked={isLocked}
                        lifted={drag?.athleteId === assigned.id}
                        onPointerDown={onPointerDownName}
                      />
                    ) : (
                      <span className="locker-case__slot-empty">Drop name</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="locker-archive__pool-label" aria-hidden>
          Drag nameplates onto the lockers
        </div>

        <div className="locker-archive__pool" data-name-pool="1">
          {nameOrder.map((id) => {
            const placed = Object.values(placements).includes(id);
            if (placed) {
              return (
                <div
                  key={id}
                  className="locker-name locker-name--pool-spacer"
                  aria-hidden
                />
              );
            }
            return (
              <NameTag
                key={id}
                athlete={athleteById[id]}
                lifted={drag?.athleteId === id}
                onPointerDown={onPointerDownName}
              />
            );
          })}
        </div>

        {errorMsg ? (
          <p className="locker-archive__error" role="alert">
            {errorMsg}
          </p>
        ) : null}

        {status !== "success" ? (
          <button
            type="button"
            className="locker-archive__submit"
            style={{ backgroundImage: `url(${greenBtn})` }}
            disabled={status === "saving"}
            onClick={handleSubmit}
          >
            {status === "saving" ? "SAVING…" : "RESTORE ARCHIVE"}
          </button>
        ) : null}
      </div>

      {drag && dragAthlete ? (
        <DragGhost athlete={dragAthlete} x={drag.x} y={drag.y} />
      ) : null}

      {inspectLocker ? (
        <div
          className="locker-inspect"
          role="dialog"
          aria-modal="true"
          aria-label={`Locker ${inspectLocker.number} contents`}
        >
          <button
            type="button"
            className="locker-inspect__backdrop"
            aria-label="Close"
            onClick={() => setInspectId(null)}
          />
          <div
            className="locker-inspect__panel"
            style={{ backgroundImage: `url(${objectiveBox})` }}
          >
            <h3 className="locker-inspect__title">
              Locker {inspectLocker.number}
            </h3>
            <img
              src={inspectLocker.image}
              alt=""
              className="locker-inspect__img"
            />
            <p className="locker-inspect__clues">
              {inspectLocker.clues.join(", ")}
            </p>
            <button
              type="button"
              className="locker-inspect__close"
              onClick={() => setInspectId(null)}
            >
              Close
            </button>
          </div>
        </div>
      ) : null}

      {status === "success" ? (
        <div className="locker-success" role="dialog" aria-modal="true">
          <div className="locker-success__veil" aria-hidden />
          <div className="locker-success__card">
            <h2 className="locker-success__title">The lockers are complete.</h2>
      
            <p className="locker-success__sub">
              A crossword has appeared.
            </p>
            <div className="locker-success__stats">
              <span>{formatSecondsToClock(elapsedSec)}</span>
              <span>+{LOCKER_ARCHIVE_POINTS}</span>
            </div>
            <button
              type="button"
              className="locker-archive__submit"
              style={{ backgroundImage: `url(${greenBtn})` }}
              onClick={() => navigate("/hall-1")}
            >
              CONTINUE
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
